import type { FieldPresentationBlock } from '#layers/data-model/constants/fields';
import type { PrimitiveFieldTypeTemplate, PrimitiveTypeTemplateResolverContext } from '#layers/data-model/types/field-types/field-type';
import { callOrReturn } from '#layers/base/public';
import { getBlockRecommendedDisplayIds } from '#layers/block-engine/public';
import { getPrimitiveFieldPresentationDefaults } from '#layers/data-model/constants/fields';

interface ResolveDisplayRecommendationsInput {
	fieldType?: PrimitiveFieldTypeTemplate | null;
	context: PrimitiveTypeTemplateResolverContext;
	inputRecommendationIds: readonly string[];
	typeOptions?: Record<string, unknown> | null;
}

interface ResolveDefaultDisplayBlockInput extends Omit<ResolveDisplayRecommendationsInput, 'inputRecommendationIds'> {
	inputId?: string | null;
}

/** Resolve ordered input and field-type recommendations with their default display. */
export function resolveDisplayRecommendations({
	fieldType,
	context,
	inputRecommendationIds,
	typeOptions,
}: ResolveDisplayRecommendationsInput): {
	recommendedDisplayIds: string[];
	defaultDisplay: FieldPresentationBlock;
} {
	const primitiveDefault = getPrimitiveFieldPresentationDefaults(context.primitiveType).display;
	const fieldTypeRecommendation = callOrReturn(fieldType?.recommendedDisplay, {
		options: typeOptions ?? undefined,
		context,
	});
	const recommendedDisplayIds = [...new Set(
		[...inputRecommendationIds, fieldTypeRecommendation].filter((id): id is string => !!id),
	)];
	const block = recommendedDisplayIds[0] ?? primitiveDefault.block;

	return {
		recommendedDisplayIds,
		defaultDisplay: block === primitiveDefault.block ? primitiveDefault : { block },
	};
}

/** Resolve the default display from input, field type, then primitive-type recommendations. */
export function resolveDefaultDisplayBlock({ inputId, ...input }: ResolveDefaultDisplayBlockInput): FieldPresentationBlock {
	return resolveDisplayRecommendations({
		...input,
		inputRecommendationIds: getBlockRecommendedDisplayIds(inputId, {
			typeOptions: input.typeOptions ?? undefined,
		}),
	}).defaultDisplay;
}
